import React, {useEffect, useState} from "react";
import {getDefaultStartPath, getFromLocalStorage, isEmail, Logout, saveEmailToLocalStorage, setCookie, valueIfNull} from "../../Hooks/UseGenericFunctions";
import {saveKeyAccountNameToLocaleStorage, savePartnerInfo, saveUserInformationToLocaleStorage} from "../../Hooks/UseBaseData";
import {axiosInstance} from "../../Api/AxiosConfig";
import {useLocation} from "react-router-dom";
import {useGlobalDataContext} from "../../GlobalState/GlobalDataProvider";
import {registerUserActivity} from "../../Api/StatService";
import {ArrowRightIcon} from "@heroicons/react/24/outline";

export const Login = () => {
    const [errorText, setErrorText] = useState('');
    const location = useLocation();
    const [email, setEmail] = useState(valueIfNull(getFromLocalStorage('CARZELLE_LOGIN_EMAIL'), ''));
    const [password, setPassword] = useState('');
    const {fetchGlobalData, loggedIn} = useGlobalDataContext();

    const redirectAfterLogin = () => {
        const query = sessionStorage.getItem("redirect_after_login_query") || '';
        const page = sessionStorage.getItem("redirect_after_login_page") || 'dashboard/carlist';

        sessionStorage.removeItem("redirect_after_login_query");
        sessionStorage.removeItem("redirect_after_login_page");
        const redirectUrl = '/' + page + (query ? '?' + query : '');
        window.location.href = redirectUrl;
    }

    const LoginWithUserNamePassword = () => {
        axiosInstance.post('auth/login', {
            username: email,
            password: password
        })
            .then(function (response) {
                savePartnerInfo(response.data);
                saveUserInformationToLocaleStorage(response.data.userData);
                saveKeyAccountNameToLocaleStorage(response.data.keyAccountUser);
                setCookie('CARZELLE_API_TOKEN', response.data.token, 2);
                fetchGlobalData();
                registerUserActivity(1, 'Logged in username/password');
            })
            .catch(function (error) {
                console.log(error);
                setErrorText('Wrong email address or password');
            });
    }

    const LoginWithQuickLink = (hash) => {
        Logout();
        axiosInstance.post('auth/ql', {
            hash: hash
        })
            .then(function (response) {
                savePartnerInfo(response.data);
                saveUserInformationToLocaleStorage(response.data.userData);
                saveKeyAccountNameToLocaleStorage(response.data.keyAccountUser);
                setCookie('CARZELLE_API_TOKEN', response.data.token, 2);
                fetchGlobalData();
                registerUserActivity(2, 'Quick login');
            })
            .catch(function (error) {
                alert('The link has expired. Please enter your email and password to login');
            });
    }

    const HandleLoginButtonClick = () => {
        if (isEmail(email)) {
            saveEmailToLocalStorage(email);
            setErrorText('');
            LoginWithUserNamePassword();
        } else {
            setErrorText('Please enter a valid e-mail address');
        }
    }

    useEffect(() => {
        if (loggedIn) {
            redirectAfterLogin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedIn]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const hash = searchParams.get("q");

        sessionStorage.setItem("redirect_after_login_query", searchParams.toString());
        sessionStorage.setItem("redirect_after_login_page", searchParams.get("page") || getDefaultStartPath());

        if (hash !== null) {
            searchParams.delete("q");
            LoginWithQuickLink(hash);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const readyToLogin = () => {
        return isEmail(email) && password.length > 0;
    }

    return (
        <>
            <div className="overflow-auto h-screen w-screen relative bg-[#5D5D5D]" style={{backgroundImage: 'url(/images/background-login.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
                <div className="w-full flex flex-row items-center justify-center pt-[34px]"><img src="/images/logo-carzelle-yellow.svg" alt="carzelle logo"/></div>
                <div className="w-full h-full flex items-start justify-center mt-[50px]">
                    <div className='screen-small:w-[800px] h-[610px] rounded-3xl bg-[#383838] shadow-lg flex items-center justify-center'>
                        <div className='mt-[30px] w-full mx-28'>
                            <div className='text-base-color-yellow text-info text-center font-medium text-[55px] mb-8'>Log In</div>
                            <div className='text-[16px] text-white font-normal mb-[35px]'>Please enter your e-mail and password to log in to the system</div>
                            <form id="loginForm">
                                <div className='h-[50px] flex items-center'>
                                    <input id='email' type="text" autoComplete="user-name" className='w-full border-b-2 border-[#9C9C9C] bg-transparent text-white px-0 py-2 focus:outline-none placeholder-white' placeholder='E-mail' autoFocus={true} value={email} onChange={(e) => setEmail(e.target.value)}/>
                                </div>
                                {errorText && <div className='text-red-400 text-sm'>{errorText}</div>}
                                <div className='h-[50px] flex items-center screen-small:mt-[32px] screen-small:mb-[10px]'>
                                    <input id='password' type="password" autoComplete="current-password" className='w-full border-b-2 border-[#9C9C9C] bg-transparent text-white px-0 py-2 focus:outline-none placeholder-white' placeholder='Password' value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            HandleLoginButtonClick();
                                        }
                                    }}/>
                                </div>
                                {errorText && <div className='text-red-400 text-sm'>{errorText}</div>}
                                <div className='mb-4 w-full flex items-center justify-center'>
                                    <label className="underline text-base text-[#9C9C9C]"><a href="/forgottenpassword">Forgot password</a></label>
                                </div>
                                <button type="button" className={`w-full mb-2 mt-4 p-3 rounded-full ${readyToLogin() ? 'bg-base-color-yellow' : 'bg-gray-300'}`} disabled={!readyToLogin()} onClick={() => HandleLoginButtonClick()}>
                                    <div className="flex items-center justify-center">
                                        <div className="mr-2">Log in</div>
                                        <div>
                                            <ArrowRightIcon className="w-4 h-4"/>
                                        </div>
                                    </div>
                                </button>
                            </form>
                            <div className='mt-4 mb-4 text-base text-white w-full text-center'>Don't have an account? <a className="underline text-base-color-yellow" href="https://signup.carzelle.com">Sign-up here</a></div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}